/*
  <otw-marquee> — seamless auto-scrolling strip (PRD §9).

  <otw-marquee data-speed="40" data-direction="reverse" aria-label="Shop by category">
    <div class="otw-marquee__track">
      <div class="otw-marquee__group">…items…</div>
    </div>
  </otw-marquee>

  - Motion is a pure CSS keyframe on the track. JS clones the group until the strip covers the
    viewport at any width, and sets the duration so the speed is a constant `data-speed` px/s.
  - Pauses on hover, keyboard focus and touch; pauses while off-screen.
  - Pointer/touch drag scrubs the strip (via the Web Animations API on the CSS animation) and
    auto-play resumes 2s after release.
  - Under prefers-reduced-motion the strip does not move and becomes a normal horizontal scroller.
*/
(() => {
  if (customElements.get('otw-marquee')) return;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const RESUME_DELAY = 2000;

  class OtwMarquee extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.otw-marquee__track');
      this.group = this.track && this.track.querySelector('.otw-marquee__group');
      if (!this.group || this.abort) return;
      this.abort = new AbortController();
      this.holds = new Set();

      this.resizeObserver = new ResizeObserver(() => this.layout());
      this.resizeObserver.observe(this);
      this.resizeObserver.observe(this.group);

      this.intersection = new IntersectionObserver(([entry]) => {
        entry.isIntersecting ? this.release('offscreen') : this.hold('offscreen');
      });
      this.intersection.observe(this);

      const on = (target, type, fn, opts = {}) =>
        target.addEventListener(type, fn, { ...opts, signal: this.abort.signal });

      on(this, 'mouseenter', () => this.hold('hover'));
      on(this, 'mouseleave', () => this.release('hover'));
      on(this, 'focusin', () => this.hold('focus'));
      on(this, 'focusout', (e) => !this.contains(e.relatedTarget) && this.release('focus'));
      on(reduced, 'change', () => this.layout());
      this.setupDrag(on);
      this.layout();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
      this.resizeObserver?.disconnect();
      this.intersection?.disconnect();
      clearTimeout(this.resumeTimer);
    }

    get speed() {
      const value = parseFloat(this.dataset.speed);
      return Number.isFinite(value) && value > 0 ? value : 40;
    }

    get animation() {
      return this.track.getAnimations ? this.track.getAnimations().find((a) => a.animationName === 'otw-marquee') : null;
    }

    layout() {
      const groupWidth = this.group.getBoundingClientRect().width;
      if (!groupWidth) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const distance = groupWidth + gap;

      this.classList.toggle('is-reduced', reduced.matches);
      if (reduced.matches) {
        this.removeClones();
        return;
      }

      // Enough copies that the strip still covers the viewport after moving one group left
      const needed = Math.max(1, Math.ceil(this.clientWidth / distance)) + 1;
      const clones = this.track.querySelectorAll('.otw-marquee__group[data-clone]');
      if (clones.length < needed) {
        for (let i = clones.length; i < needed; i++) {
          const clone = this.group.cloneNode(true);
          clone.setAttribute('data-clone', '');
          clone.setAttribute('aria-hidden', 'true');
          clone.inert = true;
          this.track.appendChild(clone);
        }
      } else {
        Array.from(clones).slice(needed).forEach((c) => c.remove());
      }

      const progress = this.progress();
      this.style.setProperty('--otw-marquee-distance', `${distance}px`);
      this.style.setProperty('--otw-marquee-duration', `${(distance / this.speed).toFixed(2)}s`);
      this.classList.add('is-ready');
      // Keep the visual position when the duration changes (resize)
      if (progress != null) requestAnimationFrame(() => this.setProgress(progress));
    }

    removeClones() {
      this.track.querySelectorAll('.otw-marquee__group[data-clone]').forEach((c) => c.remove());
    }

    progress() {
      const anim = this.animation;
      const duration = anim && anim.effect && anim.effect.getTiming().duration;
      if (!anim || !duration || anim.currentTime == null) return null;
      return (anim.currentTime % duration) / duration;
    }

    setProgress(fraction) {
      const anim = this.animation;
      const duration = anim && anim.effect && anim.effect.getTiming().duration;
      if (!anim || !duration) return;
      anim.currentTime = (((fraction % 1) + 1) % 1) * duration;
    }

    hold(reason) {
      this.holds.add(reason);
      this.classList.add('is-paused');
    }

    release(reason) {
      this.holds.delete(reason);
      if (!this.holds.size) this.classList.remove('is-paused');
    }

    /* Drag to scrub (mouse and touch), resume 2s after release */
    setupDrag(on) {
      let active = false;
      let moved = false;
      let lastX = 0;

      on(this, 'pointerdown', (event) => {
        if (reduced.matches || (event.pointerType === 'mouse' && event.button !== 0)) return;
        active = true;
        moved = false;
        lastX = event.clientX;
        clearTimeout(this.resumeTimer);
        this.hold('drag');
      });

      on(window, 'pointermove', (event) => {
        if (!active) return;
        const dx = event.clientX - lastX;
        if (!moved && Math.abs(dx) < 4) return;
        if (!moved) {
          moved = true;
          this.classList.add('is-dragging');
        }
        lastX = event.clientX;
        const distance = parseFloat(this.style.getPropertyValue('--otw-marquee-distance')) || 1;
        const current = this.progress() ?? 0;
        // Dragging right moves content right. Reverse strips run the keyframes backwards.
        const direction = this.dataset.direction === 'reverse' ? 1 : -1;
        this.setProgress(current + (direction * dx) / distance);
      });

      const end = () => {
        if (!active) return;
        active = false;
        this.classList.remove('is-dragging');
        clearTimeout(this.resumeTimer);
        this.resumeTimer = setTimeout(() => this.release('drag'), RESUME_DELAY);
      };
      on(window, 'pointerup', end);
      on(window, 'pointercancel', end);

      // Touch without drag: hold while touching, resume after 2s
      on(this, 'touchstart', () => this.hold('touch'), { passive: true });
      on(
        this,
        'touchend',
        () => {
          clearTimeout(this.touchTimer);
          this.touchTimer = setTimeout(() => this.release('touch'), RESUME_DELAY);
        },
        { passive: true }
      );

      on(this, 'dragstart', (e) => e.preventDefault());
      on(
        this,
        'click',
        (event) => {
          if (moved) {
            event.preventDefault();
            event.stopPropagation();
            moved = false;
          }
        },
        { capture: true }
      );
    }
  }

  customElements.define('otw-marquee', OtwMarquee);
})();
